import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistMissionsConnectionFragmentDoc,
  ConsoleMissionsQuery,
  ConsoleMissionsQueryVariables,
  DeleteMissionMutation,
  DeleteMissionMutationVariables,
  MissionType,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort') === OrderBy.Asc ? OrderBy.Asc : OrderBy.Desc
  const [condition, setCondition] = useState<{
    orderBy: OrderBy
    typeFilter?: MissionType
  }>({
    orderBy: sort,
    typeFilter: undefined,
  })

  const { data, loading, fetchMore, refetch } = useQuery<
    ConsoleMissionsQuery,
    ConsoleMissionsQueryVariables
  >(QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: { orderBy: condition.orderBy, typeFilter: undefined },
  })

  const [deleteMission, { loading: deletingMission }] = useMutation<
    DeleteMissionMutation,
    DeleteMissionMutationVariables
  >(DELETE_MISSION)

  return {
    state: {
      user,
      artistId: user?.uid ?? '',
      missions: data?.artistMissions ?? null,
      loading,
      draftItem: null,
      artist: data?.artist ?? null,
      deletingMission,
      hasNextPage: !!data?.artistMissions?.pageInfo.hasNextPage,
      sort: condition.orderBy,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onSearch({ orderBy, typeFilter }) {
        setCondition({ orderBy, typeFilter })
        await refetch({
          orderBy,
          typeFilter: typeFilter ? [typeFilter] : undefined,
        })
      },
      async onDelete({ id }) {
        await deleteMission({ variables: { id } })
      },
      async onDraftConfirm() {},
      async onFetchMore() {
        await fetchMore({
          variables: {
            orderBy: condition.orderBy,
            typeFilter: condition.typeFilter
              ? [condition.typeFilter]
              : undefined,
            pageInput: { cursor: data?.artistMissions?.pageInfo.endCursor },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMissions(
    $orderBy: OrderBy
    $pageInput: PageInput
    $typeFilter: [MissionType!]
  ) {
    artistMissions(
      orderBy: $orderBy
      pageInput: $pageInput
      typeFilter: $typeFilter
    ) {
      ...ArtistMissionsConnection
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${ArtistMissionsConnectionFragmentDoc}
`

const DELETE_MISSION = gql`
  mutation deleteMission($id: ID!) {
    deleteMission(id: $id) {
      id
    }
  }
`
